import type { Industry, Role, User } from "../api/types";

/** Who reaches which screen (§2.1 route map, §6.1 nav, §4.1 matrix). The UI gates for
 * convenience only; the server enforces every rule (§16.5). */

export type NavKey = "checkout" | "till" | "sales" | "products" | "categories" | "inventory" | "fuel" | "customers" | "mpesa" | "reports" | "settings" | "audit" | "queue";

export type NavItem = { key: NavKey; label: string; moreLabel: string; path: string; roles: readonly Role[]; industry?: Industry; keywords?: string };

const ALL: readonly Role[] = ["ADMIN", "MANAGER", "CASHIER"];
const BACK: readonly Role[] = ["ADMIN", "MANAGER"];
const ADMIN: readonly Role[] = ["ADMIN"];

/** Sidebar order (§6.1). `moreLabel` is the Checkout "More" menu wording (§6.2). */
export const NAV: readonly NavItem[] = [
  { key: "checkout", label: "Checkout", moreLabel: "Checkout", path: "/", roles: ALL, keywords: "pos sell sale register" },
  { key: "till", label: "Till", moreLabel: "Till / cash session", path: "/till", roles: ALL, keywords: "cash drawer float shift" },
  { key: "sales", label: "Sales History", moreLabel: "Sales history", path: "/sales", roles: ALL, keywords: "receipts refunds returns void" },
  { key: "products", label: "Products", moreLabel: "Products", path: "/products", roles: BACK, keywords: "catalogue items sku barcode price" },
  { key: "categories", label: "Categories", moreLabel: "Categories", path: "/categories", roles: BACK },
  { key: "inventory", label: "Inventory", moreLabel: "Inventory", path: "/inventory", roles: BACK, keywords: "stock adjust batches expiry" },
  { key: "fuel", label: "Fuel Station", moreLabel: "Fuel station", path: "/fuel", roles: BACK, industry: "PETROL_STATION", keywords: "tanks dispensers pumps meter" },
  { key: "customers", label: "Customers", moreLabel: "Customers", path: "/customers", roles: ALL, keywords: "credit loyalty points" },
  { key: "mpesa", label: "M-Pesa", moreLabel: "M-Pesa transactions", path: "/mpesa", roles: BACK, keywords: "stk safaricom payments" },
  { key: "reports", label: "Reports", moreLabel: "Reports", path: "/reports", roles: BACK, keywords: "analytics revenue sales" },
  { key: "settings", label: "Settings", moreLabel: "Settings", path: "/settings", roles: ADMIN, keywords: "branding staff permissions printer" },
  { key: "audit", label: "Audit Log", moreLabel: "Audit log", path: "/audit-log", roles: ADMIN, keywords: "activity history" },
];

/** The failed-sales review screen (plan Phase 7) is reached from the sync badge, not the nav. */
export const QUEUE_ITEM: NavItem = { key: "queue", label: "Unsynced sales", moreLabel: "Unsynced sales", path: "/sales/queue", roles: BACK, keywords: "offline sync failed queue" };

export function canSee(user: Pick<User, "role" | "industry"> | null | undefined, item: Pick<NavItem, "roles" | "industry">): boolean {
  if (!user) return false;
  if (!item.roles.includes(user.role)) return false;
  if (item.industry && user.industry !== item.industry) return false;
  return true;
}

export const visibleNav = (user: Pick<User, "role" | "industry"> | null | undefined): NavItem[] => NAV.filter((i) => canSee(user, i));

/** Where a user lands after sign-in (§5.1). A Superadmin belongs in pos-admin. */
export const homePath = (user: Pick<User, "role">): string => (user.role === "SUPERADMIN" ? "/platform" : "/");
